import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';

export const THREAD_TEMP_QUOTA_EXCEEDED = 'THREAD_TEMP_QUOTA_EXCEEDED';

export type ThreadTempReserveResult = {
  attachmentId: string;
  storagePath: string;
  expiresAtMillis: number;
  usedBytes: number;
  limitBytes: number;
};

export type ThreadTempListItem = {
  id: string;
  fileName: string;
  size: number;
  mimeType: string;
  createdAtMillis: number | null;
  expiresAtMillis: number | null;
  createdBy: string;
  threadId: string;
  threadTitle: string;
  threadStatus: string;
};

export type ThreadTempListResult = {
  items: ThreadTempListItem[];
  usedBytes: number;
  limitBytes: number;
};

export type ThreadTempDeleteResult = {
  deleted: string[];
  skipped: string[];
  failed: string[];
  freedBytes: number;
};

/** 一時ファイルの合計容量が上限に達して予約できなかったときのエラー。 */
export class ThreadTempQuotaError extends Error {
  readonly usedBytes: number;
  readonly limitBytes: number;
  readonly requiredBytes: number;
  readonly workspaceId: string;
  constructor(args: { workspaceId: string; usedBytes: number; limitBytes: number; requiredBytes: number }) {
    super('THREAD_TEMP_QUOTA_EXCEEDED');
    this.name = 'ThreadTempQuotaError';
    this.workspaceId = args.workspaceId;
    this.usedBytes = args.usedBytes;
    this.limitBytes = args.limitBytes;
    this.requiredBytes = args.requiredBytes;
  }
}

export const reserveThreadTempUpload = async (args: {
  workspaceId: string;
  threadId: string;
  targetType: 'post' | 'comment';
  targetId: string;
  fileName: string;
  mimeType: string;
  size: number;
}): Promise<ThreadTempReserveResult> => {
  const fn = httpsCallable<typeof args, ThreadTempReserveResult>(functions, 'reserveThreadTempUploadFn');
  try {
    const res = await fn(args);
    return res.data;
  } catch (e: any) {
    const details = e?.details;
    if (e?.code === 'functions/resource-exhausted' && details?.code === THREAD_TEMP_QUOTA_EXCEEDED) {
      throw new ThreadTempQuotaError({
        workspaceId: args.workspaceId,
        usedBytes: Number(details.usedBytes) || 0,
        limitBytes: Number(details.limitBytes) || 0,
        requiredBytes: Number(details.requiredBytes) || args.size,
      });
    }
    throw e;
  }
};

export const confirmThreadTempUpload = async (args: {
  workspaceId: string;
  attachmentId: string;
  downloadURL: string;
}): Promise<void> => {
  const fn = httpsCallable<typeof args, { ok: boolean }>(functions, 'confirmThreadTempUploadFn');
  await fn(args);
};

export const listThreadTempFiles = async (workspaceId: string): Promise<ThreadTempListResult> => {
  const fn = httpsCallable<{ workspaceId: string }, ThreadTempListResult>(functions, 'listThreadTempFilesFn');
  const res = await fn({ workspaceId });
  return res.data;
};

export const deleteThreadTempFiles = async (args: {
  workspaceId: string;
  attachmentIds: string[];
}): Promise<ThreadTempDeleteResult> => {
  const fn = httpsCallable<typeof args, ThreadTempDeleteResult>(functions, 'deleteThreadTempFilesFn');
  const res = await fn(args);
  return res.data;
};
